import { useState } from 'react';
import { Clock, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/cn';
import type { Assessment } from '@/lib/api/lms';
import { richTextToPlain } from '@/components/RichText';

const ASKED_IN_VISIBLE_COUNT = 5;

/** Caps the "Asked in" list to a handful of pills and discloses the rest on
 *  demand — a raw dump of 15-35 company pills reads as noise, not signal. */
function AskedIn({ companies }: { companies: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = companies.length - ASKED_IN_VISIBLE_COUNT;
  const visible = expanded ? companies : companies.slice(0, ASKED_IN_VISIBLE_COUNT);

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-muted-foreground">Asked in:</span>
      {visible.map((company) => (
        <span key={company} className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{company}</span>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((v) => !v); }}
          className="rounded-full bg-muted px-2 py-0.5 font-medium text-primary hover:underline"
        >
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      )}
    </span>
  );
}

// Difficulty uses universal semantic colors (not brand-blue). Type is a neutral chip.
const difficultyColors: Record<string, string> = {
  easy: 'text-green-600',
  medium: 'text-amber-600',
  hard: 'text-red-600',
};

// CTA per type. Every card now links out to a dedicated screen (problem solver,
// quiz page, or the Practice assignment/project detail) — no inline brief/submit.
const CTA_LABEL: Record<string, string> = {
  mcq_quiz: 'Take quiz',
  coding_challenge: 'Start challenge',
  project_assignment: 'View details',
};

const CTA_SUBMITTED_LABEL: Record<string, string> = {
  mcq_quiz: 'View results',
  coding_challenge: 'View submission',
  project_assignment: 'View submission',
};

interface PracticeSectionProps {
  assessments: Assessment[];
  batchId: string;
  topicId: string;
}

export function PracticeSection({ assessments, batchId, topicId }: PracticeSectionProps) {
  if (!assessments || assessments.length === 0) {
    return (
      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-2 font-heading text-base font-bold">Practice &amp; Assessments</h2>
        <p className="text-sm text-muted-foreground">No assessments for this topic yet.</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="mb-3 font-heading text-base font-bold">Practice &amp; Assessments ({assessments.length})</h2>
      <div className="space-y-2">
        {assessments.map((a) => {
          const due = a.due_date ? new Date(a.due_date) : null;
          const overdue = due && !a.submitted ? due.getTime() < Date.now() : false;
          const ctaLabel = a.submitted ? CTA_SUBMITTED_LABEL[a.type] : CTA_LABEL[a.type];
          const isSolvedCoding = a.type === 'coding_challenge' && !!a.solved;
          const label = isSolvedCoding ? 'Solved' : ctaLabel;
          const excerpt = richTextToPlain(a.description);

          // Header + stats only — this is what's clickable, wrapped in a Link below.
          // "Asked in" is rendered as a sibling outside the Link: it has its own
          // "+N more" button, and a <button> nested inside an <a> is both invalid
          // HTML and swallows clicks into the surrounding link's navigation.
          const cardContent = (
            <>
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground group-hover:text-primary">{a.title}</p>
                  {excerpt && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{excerpt}</p>}
                </div>
                <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">{a.type.replace(/_/g, ' ')}</span>
                  {a.difficulty && (
                    <span className={cn('text-xs font-semibold capitalize', difficultyColors[a.difficulty.toLowerCase()] ?? 'text-muted-foreground')}>
                      {a.difficulty}
                    </span>
                  )}
                  {label && (
                    <span className={cn('inline-flex items-center gap-0.5 text-xs font-semibold', isSolvedCoding || a.submitted ? 'text-green-600' : 'text-primary')}>
                      {label}
                      {isSolvedCoding ? <CheckCircle2 className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </span>
                  )}
                </div>
              </div>
              {(a.max_score != null || due) && (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {a.max_score != null && <span>Max: {a.max_score} pts</span>}
                  {due && (
                    <span className={cn('flex items-center gap-1', overdue ? 'font-semibold text-red-600' : 'text-amber-600')}>
                      <Clock className="h-3 w-3" />
                      {overdue ? 'Overdue: ' : 'Due: '}{due.toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </>
          );

          const askedIn = a.asked_in && a.asked_in.length > 0 && (
            <div className="mt-2 text-xs">
              <AskedIn companies={a.asked_in} />
            </div>
          );

          const linkClass = 'group block';

          // Coding challenges open the problem solver (carrying batch/topic context).
          if (a.type === 'coding_challenge' && a.problem_slug) {
            return (
              <div key={a.id} className="rounded-lg border border-border p-3 transition-colors hover:bg-muted/30">
                <Link to="/problems/$slug" params={{ slug: a.problem_slug }} search={{ batchId, topicId }} className={linkClass}>
                  {cardContent}
                </Link>
                {askedIn}
              </div>
            );
          }

          // Assignments & projects open the Practice detail page (brief + submit live there).
          if (a.type === 'project_assignment') {
            const category = a.assignment_kind === 'project' ? 'projects' : 'assignments';
            return (
              <div key={a.id} className="rounded-lg border border-border p-3 transition-colors hover:bg-muted/30">
                <Link
                  to="/courses/$batchId/practice/$category/$assessmentId"
                  params={{ batchId, category, assessmentId: a.id }}
                  className={linkClass}
                >
                  {cardContent}
                </Link>
                {askedIn}
              </div>
            );
          }

          // Quizzes (and any other in-app takeable type) open the quiz page.
          if (CTA_LABEL[a.type]) {
            return (
              <div key={a.id} className="rounded-lg border border-border p-3 transition-colors hover:bg-muted/30">
                <Link
                  to="/courses/$batchId/topics/$topicId/quiz/$assessmentId"
                  params={{ batchId, topicId, assessmentId: a.id }}
                  // Launched from the topic's own Practice tab, so the exit
                  // returns here rather than to the batch-wide list.
                  search={{ from: 'topic' as const }}
                  className={linkClass}
                >
                  {cardContent}
                </Link>
                {askedIn}
              </div>
            );
          }

          return (
            <div key={a.id} className="rounded-lg border border-border p-3">
              {cardContent}
              {askedIn}
            </div>
          );
        })}
      </div>
    </section>
  );
}
